import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import RcInstanceDTO from '@robocorp/robocloud-internal-cds-sdk/lib/sdk/Types';
import { RcInstanceDB } from '@robocorp/robocloud-internal-cds-sdk/lib/src/types';
import { ApiResponse } from 'apisauce';
import { activityTemplateService } from '../services/activityTemplateAPI';
import { OkAction, ErrorAction, Editing } from './baseActions';
import { formatApiErrorPayload } from './utils';
import { selectCurrentWorkspaceId } from './utils/selectors';

/* eslint-disable @typescript-eslint/no-non-null-assertion */

type AppState = import('~/store').AppState;

export enum ActivityTemplateActionType {
  CREATE_ACTIVITY_TEMPLATE = 'CREATE_ACTIVITY_TEMPLATE',
  CREATE_ACTIVITY_TEMPLATE_OK = 'CREATE_ACTIVITY_TEMPLATE_OK',
  CREATE_ACTIVITY_TEMPLATE_ERROR = 'CREATE_ACTIVITY_TEMPLATE_ERROR',
  SILENT_GET_ACTIVITY_TEMPLATE = 'SILENT_GET_ACTIVITY_TEMPLATE',
  GET_ACTIVITY_TEMPLATE = 'GET_ACTIVITY_TEMPLATE',
  GET_ACTIVITY_TEMPLATE_OK = 'GET_ACTIVITY_TEMPLATE_OK',
  GET_ACTIVITY_TEMPLATE_ERROR = 'GET_ACTIVITY_TEMPLATE_ERROR',
  EDIT_ACTIVITY_TEMPLATE = 'EDIT_ACTIVITY_TEMPLATE',
  EDIT_ACTIVITY_TEMPLATE_OK = 'EDIT_ACTIVITY_TEMPLATE_OK',
  EDIT_ACTIVITY_TEMPLATE_ERROR = 'EDIT_ACTIVITY_TEMPLATE_ERROR',
  SILENT_GET_ACTIVITY_TEMPLATES = 'SILENT_GET_ACTIVITY_TEMPLATES',
  GET_ACTIVITY_TEMPLATES = 'GET_ACTIVITY_TEMPLATES',
  GET_ACTIVITY_TEMPLATES_OK = 'GET_ACTIVITY_TEMPLATES_OK',
  GET_ACTIVITY_TEMPLATES_ERROR = 'GET_ACTIVITY_TEMPLATES_ERROR',
  DELETE_ACTIVITY_TEMPLATE = 'DELETE_ACTIVITY_TEMPLATE',
  DELETE_ACTIVITY_TEMPLATE_OK = 'DELETE_ACTIVITY_TEMPLATE_OK',
  DELETE_ACTIVITY_TEMPLATE_ERROR = 'DELETE_ACTIVITY_TEMPLATE_ERROR',
}

export type ActivityTemplateEditingDetails =
  | Editing<'changingName', 'editNameError'>
  | Editing<'changingSourceType', 'editSourceTypeError'>
  | Editing<'changingPackageUrl', 'editPackageUrlError'>
  | Editing<'changingPackage', 'editPackageUrlError'>;

type WithId = { id: string };

type CreateActivityTemplateAction =
  | Action<ActivityTemplateActionType.CREATE_ACTIVITY_TEMPLATE>
  | OkAction<ActivityTemplateActionType.CREATE_ACTIVITY_TEMPLATE_OK, RcInstanceDTO.ActivityTemplate>
  | ErrorAction<ActivityTemplateActionType.CREATE_ACTIVITY_TEMPLATE_ERROR>;

export type GetActivityTemplateByIdAction =
  | Action<ActivityTemplateActionType.SILENT_GET_ACTIVITY_TEMPLATE>
  | (Action<ActivityTemplateActionType.GET_ACTIVITY_TEMPLATE> & WithId)
  | (OkAction<ActivityTemplateActionType.GET_ACTIVITY_TEMPLATE_OK, RcInstanceDTO.ActivityTemplate> &
      WithId)
  | (ErrorAction<ActivityTemplateActionType.GET_ACTIVITY_TEMPLATE_ERROR> & WithId);

type EditActivityTemplateAction =
  | (Action<ActivityTemplateActionType.EDIT_ACTIVITY_TEMPLATE> &
      WithId &
      ActivityTemplateEditingDetails)
  | (OkAction<
      ActivityTemplateActionType.EDIT_ACTIVITY_TEMPLATE_OK,
      RcInstanceDTO.ActivityTemplate
    > &
      WithId &
      ActivityTemplateEditingDetails)
  | (ErrorAction<ActivityTemplateActionType.EDIT_ACTIVITY_TEMPLATE_ERROR> &
      WithId &
      ActivityTemplateEditingDetails);

type GetActivityTemplatesAction =
  | Action<ActivityTemplateActionType.SILENT_GET_ACTIVITY_TEMPLATES>
  | Action<ActivityTemplateActionType.GET_ACTIVITY_TEMPLATES>
  | OkAction<ActivityTemplateActionType.GET_ACTIVITY_TEMPLATES_OK, RcInstanceDTO.ActivityTemplate[]>
  | ErrorAction<ActivityTemplateActionType.GET_ACTIVITY_TEMPLATES_ERROR>;

type DeleteActivityTemplateAction =
  | (Action<ActivityTemplateActionType.DELETE_ACTIVITY_TEMPLATE> & WithId)
  | (OkAction<
      ActivityTemplateActionType.DELETE_ACTIVITY_TEMPLATE_OK,
      RcInstanceDTO.ActivityTemplate
    > &
      WithId)
  | (ErrorAction<ActivityTemplateActionType.DELETE_ACTIVITY_TEMPLATE_ERROR> & WithId);

export type ActivityTemplateAction =
  | CreateActivityTemplateAction
  | GetActivityTemplateByIdAction
  | EditActivityTemplateAction
  | GetActivityTemplatesAction
  | DeleteActivityTemplateAction;

export const getActivityTemplates =
  (): ThunkAction<void, AppState, null, GetActivityTemplatesAction> =>
  async (dispatch, getState) => {
    if (getState().workspaceResources.activityTemplates.allIds.length > 0) {
      dispatch({
        type: ActivityTemplateActionType.SILENT_GET_ACTIVITY_TEMPLATES,
      });
    } else {
      dispatch({ type: ActivityTemplateActionType.GET_ACTIVITY_TEMPLATES });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityTemplateService.getActivityTemplates({ workspaceId });
    if (response.ok) {
      dispatch({
        type: ActivityTemplateActionType.GET_ACTIVITY_TEMPLATES_OK,
        data: response.data as RcInstanceDTO.ActivityTemplate[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityTemplateActionType.GET_ACTIVITY_TEMPLATES_ERROR,
      });
    }
  };

export const getActivityTemplateById =
  (id: string): ThunkAction<void, AppState, null, GetActivityTemplateByIdAction> =>
  async (dispatch, getState) => {
    if (getState().workspaceResources.activityTemplates.byId[id]) {
      dispatch({
        type: ActivityTemplateActionType.SILENT_GET_ACTIVITY_TEMPLATE,
        id,
      });
    } else {
      dispatch({ type: ActivityTemplateActionType.GET_ACTIVITY_TEMPLATE, id });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityTemplateService.getActivityTemplateById({ workspaceId, id });
    if (response.ok) {
      dispatch({
        id,
        type: ActivityTemplateActionType.GET_ACTIVITY_TEMPLATE_OK,
        data: response.data as RcInstanceDTO.ActivityTemplate,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityTemplateActionType.GET_ACTIVITY_TEMPLATE_ERROR,
        id,
      });
    }
  };

export const createActivityTemplate =
  (
    name: string,
  ): ThunkAction<
    Promise<ApiResponse<RcInstanceDTO.ActivityTemplate>>,
    AppState,
    null,
    CreateActivityTemplateAction
  > =>
  async (dispatch, getState) => {
    dispatch({ type: ActivityTemplateActionType.CREATE_ACTIVITY_TEMPLATE });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityTemplateService.createActivityTemplate({ workspaceId, name });
    if (response.ok) {
      dispatch({
        type: ActivityTemplateActionType.CREATE_ACTIVITY_TEMPLATE_OK,
        data: response.data!,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityTemplateActionType.CREATE_ACTIVITY_TEMPLATE_ERROR,
      });
    }

    return response;
  };

export const deleteActivityTemplateById =
  (
    id: string,
  ): ThunkAction<
    Promise<ApiResponse<RcInstanceDTO.ActivityTemplate>>,
    AppState,
    null,
    DeleteActivityTemplateAction
  > =>
  async (dispatch, getState) => {
    dispatch({ type: ActivityTemplateActionType.DELETE_ACTIVITY_TEMPLATE, id });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityTemplateService.deleteActivityTemplateById({ workspaceId, id });
    if (response.ok) {
      dispatch({
        id,
        type: ActivityTemplateActionType.DELETE_ACTIVITY_TEMPLATE_OK,
        data: response.data!,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityTemplateActionType.DELETE_ACTIVITY_TEMPLATE_ERROR,
        id,
      });
    }

    return response;
  };

const updateActivityTemplate =
  (
    id: string,
    update: Partial<RcInstanceDTO.ActivityTemplate>,
    editingDetails: ActivityTemplateEditingDetails,
  ): ThunkAction<
    Promise<ApiResponse<RcInstanceDB.ActivityTemplate>>,
    AppState,
    null,
    EditActivityTemplateAction
  > =>
  async (dispatch, getState) => {
    const template = getState().workspaceResources.activityTemplates.byId[id];

    if (typeof update !== 'object') {
      dispatch({
        type: ActivityTemplateActionType.EDIT_ACTIVITY_TEMPLATE_ERROR,
        id,
        error: 'UPDATE_ERROR',
        ...editingDetails,
      });
    }

    const updated = {
      ...template,
      ...update,
      // TODO s3 files cannot be updated via ui
      // if config is not updated, use the old one
      config: update?.config || template?.config,
    };

    dispatch({
      type: ActivityTemplateActionType.EDIT_ACTIVITY_TEMPLATE,
      id,
      ...editingDetails,
    });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityTemplateService.editActivityTemplate({
      workspaceId,
      template: {
        ...updated,
        id,
      },
    });

    if (response.ok) {
      dispatch({
        type: ActivityTemplateActionType.EDIT_ACTIVITY_TEMPLATE_OK,
        data: response.data!,
        id,
        ...editingDetails,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityTemplateActionType.EDIT_ACTIVITY_TEMPLATE_ERROR,
        id,
        ...editingDetails,
      });
    }

    return response;
  };

export const updateActivityTemplateName = (id: string, name: string) =>
  updateActivityTemplate(id, { name }, { statusKey: 'changingName', errorKey: 'editNameError' });

export const setActivityTemplateSourceType = (
  id: string,
  sourceType: RcInstanceDB.ActivityTemplateSourceType,
) =>
  updateActivityTemplate(
    id,
    {
      config: {
        sourceType,
      },
    },
    { statusKey: 'changingSourceType', errorKey: 'editSourceTypeError' },
  );

export const setActivityTemplateExtUrl = (id: string, url: string) =>
  updateActivityTemplate(
    id,
    {
      config: {
        packageUrl: url,
        sourceType: RcInstanceDB.ActivityTemplateSourceType.ExternalUrl,
      },
    },
    { statusKey: 'changingPackageUrl', errorKey: 'editPackageUrlError' },
  );
